import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material'
import ArrowBack from '@mui/icons-material/ArrowBack'
import EditOutlined from '@mui/icons-material/EditOutlined'
import { ApiError } from '../api/errors'
import {
  currentDescriptionKey,
  descriptionVersionsKey,
  fetchCurrentDescription,
  fetchDescriptionVersions,
  fetchJobRecord,
  findDraft,
  groupDescription,
  jobRecordKey,
  previousPublished,
  textoParaTela,
  type DescriptionItem,
  type JobRecord,
  type PublishedDescription,
  type RequirementColumns,
  type VersionSummary,
} from '../api/jobDescription'
import { descriptionKey, fetchDescription, type DescriptionVersion } from '../api/jobDescriptionDraft'
import { BANDS_QUERY_KEY, fetchBands, functionByJob, maiuscula } from '../api/bands'
import { fetchMe, ME_QUERY_KEY } from '../api/session'
import { DescriptionPdfButton } from '../components/DescriptionPdfButton'
import { CboField } from '../components/CboField'
import { JobStatusField } from '../components/JobStatusField'
import { JobFunctionField } from '../components/JobFunctionField'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import { t } from '../i18n'

/**
 * Tela 5 (sessao 38) - FICHA DO CARGO, so leitura, no layout do PES.001.
 *
 * PARA QUEM (regra 47, corolario s32): os 5 papeis leem (job.read + description.read);
 * o operador e o `hr_manager`. Nenhum botao de criar/editar aqui -- a edicao e a tela 6;
 * a ficha so leva ATE ela (link "Editar descricao", s43), e so para quem tem description.write.
 *
 * Decisoes aplicadas (s38, "OPCAO A"): D1 transversais no fim de "Entregas e atividades";
 * D2 atividades so sob a entrega (sem secao "Responsabilidades" repetida); D3 unico texto
 * fixo na tela = aviso das habilidades (observacao/aprovacoes/rodape ficam para a impressao);
 * D4 "descricao antiga" so no 404 com codigo; D5 aviso de rascunho so para quem escreve;
 * D6 secao vazia = "Nao informado"; D7 rodape = numero + data, nenhum id de usuario.
 * S27-6: SEM salario e SEM grade/spread. Rota com id opaco (ADR-0024 D10.1).
 *
 * s55 (R1, achado A6-s54): cargo SEM publicada e COM rascunho caia sempre no legado -- o conteudo do
 * rascunho nunca aparecia, nem para quem edita. Agora, para quem tem description.write, a ficha mostra o
 * RASCUNHO com selo (D5 continua: quem so le ve o legado e o rascunho nem e pedido ao servidor). Com
 * publicada + rascunho novo, a ficha segue sendo a PUBLICADA e ganha o aviso do rascunho. Falha ao ler o
 * rascunho -> volta ao legado com o aviso (nunca tela vazia).
 */

const PERM_DESCRIPTION_WRITE = 'job_architecture.description.write'

function semRepetirEm4xx(falhas: number, erro: unknown): boolean {
  if (erro instanceof ApiError && erro.status >= 400 && erro.status < 500) return false
  return falhas < 2
}

function formatarData(iso: string | null): string {
  if (!iso) return t.jobRecord.notInformed
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="dt">
        {rotulo}
      </Typography>
      <Typography variant="body2" component="dd" sx={{ m: 0, overflowWrap: 'anywhere' }}>
        {valor && valor.trim() !== '' ? valor : t.jobRecord.notInformed}
      </Typography>
    </Box>
  )
}

function Secao({ id, titulo, children }: { id: string; titulo: string; children: ReactNode }) {
  return (
    <Paper component="section" variant="outlined" aria-labelledby={`sec-${id}`} data-testid={`secao-${id}`} sx={{ p: 2 }}>
      <Typography id={`sec-${id}`} variant="h2" component="h2" sx={{ fontSize: '1.125rem', mb: 1.5 }}>
        {titulo}
      </Typography>
      {children}
    </Paper>
  )
}

function NaoInformado() {
  return (
    <Typography variant="body2" color="text.secondary">
      {t.jobRecord.notInformed}
    </Typography>
  )
}

function Lista({ itens, testId }: { itens: readonly DescriptionItem[]; testId?: string }) {
  if (itens.length === 0) return <NaoInformado />
  return (
    <Box component="ul" data-testid={testId} sx={{ m: 0, pl: 2.5 }}>
      {itens.map((i) => (
        <Typography key={i.id} component="li" variant="body2" data-testid={`item-${i.id}`} sx={{ mb: 0.5, overflowWrap: 'anywhere' }}>
          {textoParaTela(i.text)}
        </Typography>
      ))}
    </Box>
  )
}

function Colunas({ id, titulo, cols }: { id: string; titulo: string; cols: RequirementColumns }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h3" component="h3" sx={{ fontSize: '1rem', mb: 1 }}>
        {titulo}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 2 }}>
        {(['necessario', 'desejavel'] as const).map((k) => (
          <Box key={k} data-testid={`col-${id}-${k}`} sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ fontWeight: 600, mb: 0.5 }}>
              {k === 'necessario' ? t.jobRecord.colNecessario : t.jobRecord.colDesejavel}
            </Typography>
            <Lista itens={cols[k]} />
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function Ficha({ d }: { d: PublishedDescription }) {
  const g = groupDescription(d)
  const semEntregas = g.entregas.length === 0 && g.transversais.length === 0
  return (
    <Stack spacing={2}>
      <Secao id="papel" titulo={t.jobRecord.sectionPapel}>
        <Lista itens={g.papel} />
      </Secao>

      <Secao id="entregas" titulo={t.jobRecord.sectionEntregas}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t.jobRecord.sectionEntregasHint}
        </Typography>
        {semEntregas && <NaoInformado />}
        <Stack spacing={1.5}>
          {g.entregas.map(({ entrega, atividades }) => (
            <Box key={entrega.id} data-testid={`entrega-${entrega.id}`}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {textoParaTela(entrega.text)}
              </Typography>
              {atividades.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  {t.jobRecord.noActivities}
                </Typography>
              ) : (
                <Lista itens={atividades} />
              )}
            </Box>
          ))}
          {g.transversais.length > 0 && (
            <Box data-testid="transversais">
              <Typography variant="h3" component="h3" sx={{ fontSize: '1rem', mb: 0.5 }}>
                {t.jobRecord.sectionTransversais}
              </Typography>
              <Lista itens={g.transversais} />
            </Box>
          )}
        </Stack>
      </Secao>

      <Secao id="competencias" titulo={t.jobRecord.sectionCompetencias}>
        <Colunas id="formacao" titulo={t.jobRecord.sectionFormacao} cols={g.formacao} />
        <Colunas id="conhecimentos" titulo={t.jobRecord.sectionConhecimentos} cols={g.conhecimentos} />
        <Colunas id="treinamentos" titulo={t.jobRecord.sectionTreinamentos} cols={g.treinamentos} />
      </Secao>

      <Secao id="experiencia" titulo={t.jobRecord.sectionExperiencia}>
        <Lista itens={g.experiencia} />
      </Secao>

      <Secao id="soft-skills" titulo={t.jobRecord.sectionSoftSkills}>
        <Lista itens={g.softSkills} />
        <Typography variant="caption" color="text.secondary" component="p" data-testid="soft-skills-notice" sx={{ mt: 1.5 }}>
          {t.jobRecord.softSkillsNotice}
        </Typography>
      </Secao>
    </Stack>
  )
}

function Rodape({ d, versoes }: { d: PublishedDescription; versoes: readonly VersionSummary[] }) {
  const [aberto, setAberto] = useState(false)
  const anteriores = previousPublished(versoes, d.id)
  return (
    <Box component="footer" data-testid="rodape-versoes" sx={{ pt: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {t.jobRecord.versionLine(d.versionNumber, formatarData(d.publishedAt))}
      </Typography>
      {anteriores.length > 0 && (
        <>
          <Button size="small" sx={{ ml: 1 }} onClick={() => setAberto((a) => !a)}>
            {aberto ? t.jobRecord.hidePrevious : t.jobRecord.previousVersions(anteriores.length)}
          </Button>
          {aberto && (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {anteriores.map((v) => (
                <Typography key={v.id} component="li" variant="caption" color="text.secondary" display="list-item">
                  {t.jobRecord.versionLine(v.versionNumber, formatarData(v.publishedAt))}
                </Typography>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

function comoFicha(v: DescriptionVersion): PublishedDescription {
  return {
    id: v.id,
    versionNumber: v.versionNumber,
    funcao: v.funcao,
    areaName: v.areaName,
    reportsToName: v.reportsToName,
    publishedAt: null,
    items: v.items,
  }
}

function FichaRascunho({ d }: { d: PublishedDescription }) {
  return (
    <Stack spacing={1.5} data-testid="ficha-rascunho">
      <Box>
        <Chip data-testid="selo-rascunho" label={t.jobRecord.draftBadge(d.versionNumber)} color="info" variant="outlined" size="small" />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {t.jobRecord.draftHint}
      </Typography>
      <Ficha d={d} />
    </Stack>
  )
}

function Legado({ job, rascunho }: { job: JobRecord; rascunho: VersionSummary | null }) {
  const texto = job.legacyDescription?.trim() ?? ''
  return (
    <Paper variant="outlined" data-testid="legado" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Box>
          <Chip label={t.jobRecord.legacyBadge} color="warning" variant="outlined" size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {t.jobRecord.legacyHint}
        </Typography>
        {rascunho && (
          <Alert severity="info" data-testid="aviso-rascunho">
            {t.jobRecord.draftInProgress(rascunho.versionNumber)}
          </Alert>
        )}
        {texto === '' ? (
          <NaoInformado />
        ) : (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {texto}
          </Typography>
        )}
      </Stack>
    </Paper>
  )
}

export function JobRecordPage() {
  const { jobId = '' } = useParams()
  const me = useQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchMe })
  const job = useQuery({ queryKey: jobRecordKey(jobId), queryFn: () => fetchJobRecord(jobId), retry: semRepetirEm4xx })
  const atual = useQuery({
    queryKey: currentDescriptionKey(jobId),
    queryFn: () => fetchCurrentDescription(jobId),
    enabled: job.isSuccess,
    retry: semRepetirEm4xx,
  })
  const versoes = useQuery({
    queryKey: descriptionVersionsKey(jobId),
    queryFn: () => fetchDescriptionVersions(jobId),
    enabled: job.isSuccess,
    retry: semRepetirEm4xx,
  })

  const podeEscrever = (me.data?.permissions ?? []).includes(PERM_DESCRIPTION_WRITE)
  // s62 / C3b-2 (F2/F3): a FUNCAO e do CARGO (catalogo da empresa), nao da descricao
  const faixas = useQuery({ queryKey: BANDS_QUERY_KEY, queryFn: fetchBands, retry: false })
  const podeFuncao = (me.data?.permissions ?? []).includes('job_architecture.job.write')
  const rascunho = podeEscrever ? findDraft(versoes.data ?? []) : null
  const naoEncontrado = job.isError && job.error instanceof ApiError && job.error.status === 404
  // R1 (s55): o rascunho so e pedido quando NAO ha publicada e quem olha pode escrever (rascunho != null so nesse caso).
  const semPublicada = atual.isSuccess && atual.data === null
  const rascunhoCompleto = useQuery({
    queryKey: descriptionKey(rascunho?.id ?? ''),
    queryFn: () => fetchDescription(rascunho?.id ?? ''),
    enabled: semPublicada && rascunho !== null,
    // Sem novas tentativas: se falhar, a tela volta NA HORA ao que mostrava antes do R1 (legado + aviso).
    retry: false,
  })
  const aguardando = semPublicada && ((podeEscrever && versoes.isPending) || (rascunho !== null && rascunhoCompleto.isPending))
  const fichaRascunho = semPublicada && rascunho !== null && rascunhoCompleto.isSuccess ? comoFicha(rascunhoCompleto.data) : null
  const cabecalhoVersao = atual.data ?? fichaRascunho

  return (
    // overflow-wrap e HERDADO: no celular a casca deixa ~110 px uteis (achado A4-s38) -- nada pode empurrar a pagina
    <Stack spacing={2} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <Box>
        <Button size="small" component={RouterLink} to="/cargos" startIcon={<ArrowBack fontSize="small" />}>
          {t.jobRecord.back}
        </Button>
      </Box>

      {job.isPending && <LoadingState />}
      {naoEncontrado && (
        <Alert severity="warning" data-testid="cargo-nao-encontrado">
          {t.jobRecord.notFound}
        </Alert>
      )}
      {job.isError && !naoEncontrado && <ErrorState error={job.error} onRetry={() => void job.refetch()} />}

      {job.isSuccess && (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {podeEscrever && (
            <Box>
              <Button
                variant="outlined"
                size="small"
                component={RouterLink}
                to={`/cargos/${encodeURIComponent(jobId)}/descricao`}
                startIcon={<EditOutlined fontSize="small" />}
              >
                {t.jobRecord.editDescription}
              </Button>
            </Box>
          )}
          {/* s62 / PDF (P2): a publicada vigente; sem publicada, o rascunho que a ficha mostra (so a quem edita) */}
          {cabecalhoVersao && (
            <DescriptionPdfButton
              descriptionId={cabecalhoVersao.id}
              jobCode={job.data.code}
              version={cabecalhoVersao.versionNumber}
              published={atual.data != null}
            />
          )}
          </Stack>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h1" component="h1" sx={{ fontSize: '1.5rem', mb: 1.5, overflowWrap: 'anywhere' }}>
              {job.data.name}
            </Typography>
            <Box
              component="dl"
              data-testid="cabecalho"
              sx={{ m: 0, display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(min(140px, 100%), 1fr))' }}
            >
              {/* s69 / 2m (C1): o codigo interno do cargo sai da tela (continua no banco e no PDF); (C2) o CBO e digitado aqui */}
              {/* s69 / 2q: o CBO ocupa 2 colunas -- numa so, o selo espremia o numero (um digito por linha) */}
              <Box data-testid="ficha-cbo" sx={{ gridColumn: { xs: '1 / -1', sm: 'span 2' } }}>
                <Typography variant="caption" color="text.secondary" component="dt">
                  {t.jobRecord.fieldCbo}
                </Typography>
                <Box component="dd" sx={{ m: 0 }}>
                  <CboField jobId={jobId} cbo={job.data.cboCode} pode={podeFuncao} />
                </Box>
              </Box>
              {/* s69 / 2s: situacao do cargo (ativo/inativo), editavel por quem escreve cargos */}
              <Box data-testid="ficha-situacao">
                <Typography variant="caption" color="text.secondary" component="dt">
                  {t.jobRecord.fieldStatus}
                </Typography>
                <Box component="dd" sx={{ m: 0 }}>
                  <JobStatusField jobId={jobId} status={job.data.status} pode={podeFuncao} />
                </Box>
              </Box>
              {cabecalhoVersao && (
                <>
                  {/* API sem catalogo (ou leitura falhou): volta o texto da descricao, como antes da C3b-2 */}
                  {!faixas.isSuccess && <Campo rotulo={t.jobRecord.fieldFuncao} valor={cabecalhoVersao.funcao ? maiuscula(cabecalhoVersao.funcao) : null} />}
                  <Campo rotulo={t.jobRecord.fieldArea} valor={cabecalhoVersao.areaName} />
                  <Campo rotulo={t.jobRecord.fieldReportsTo} valor={cabecalhoVersao.reportsToName} />
                </>
              )}
            </Box>
            {faixas.isSuccess && (
              <Box data-testid="ficha-funcao" sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" component="p">
                  {t.jobRecord.fieldFuncao}
                </Typography>
                <JobFunctionField
                  jobId={jobId}
                  jobName={job.data.name}
                  bands={faixas.data}
                  atual={functionByJob(faixas.data).get(jobId)}
                  pode={podeFuncao}
                />
              </Box>
            )}
          </Paper>

          {(atual.isPending || aguardando) && <LoadingState />}
          {atual.isError && <ErrorState error={atual.error} onRetry={() => void atual.refetch()} />}
          {semPublicada && !aguardando && fichaRascunho === null && <Legado job={job.data} rascunho={rascunho} />}
          {fichaRascunho && <FichaRascunho d={fichaRascunho} />}
          {atual.isSuccess && atual.data !== null && (
            <>
              {rascunho && (
                <Alert severity="info" data-testid="aviso-rascunho">
                  {t.jobRecord.draftInProgress(rascunho.versionNumber)}
                </Alert>
              )}
              <Ficha d={atual.data} />
              <Rodape d={atual.data} versoes={versoes.data ?? []} />
            </>
          )}
        </>
      )}
    </Stack>
  )
}
